import CloseIcon from "@mui/icons-material/Close";
import {
  Box,
  Divider,
  Drawer,
  IconButton,
  Stack,
  Typography,
  alpha,
  useTheme,
} from "@mui/material";
import viewpaths from "global/constants/ViewPathConstants";
import { usePathname } from "hooks/use-pathname";
import { useTranslation } from "react-i18next";
import { useDispatch, useSelector } from "react-redux";
import { useHistory } from "react-router-dom";
import darkLogo from "../../../assets/images/PanaceaInsightsLogoDark.svg";
import lightLogo from "../../../assets/images/PanaceaInsightsLogoLight.svg";
import { clearAuthUserIdFromStorage } from "../../../screens/Blogs/blogsUserPreferences";
import {
  logOutAction,
  selectAuthenticated,
  selectEmail,
  selectIsAdmin,
  selectUserName,
} from "../../../redux/authSlice";
import { Section } from "../Config";

const BRAND = "#f8481c";

interface CustomProps {
  onClose?: () => void;
  open?: boolean;
  sections?: Section[];
}

const MobileNav = (props: CustomProps) => {
  const history = useHistory();
  const dispatch = useDispatch();
  const pathname = usePathname();
  const theme = useTheme();
  const { t } = useTranslation();
  const isDark = theme.palette.mode === "dark";
  const authenticated = useSelector(selectAuthenticated);
  const isAdmin = useSelector(selectIsAdmin);
  const userName = useSelector(selectUserName);
  const email = useSelector(selectEmail);

  const navMenu = [
    { label: t("home"), url: viewpaths.landingViewPath },
    { label: t("blogs"), url: viewpaths.blogsViewPath },
    { label: t("contact"), url: viewpaths.contactViewPath },
    ...(authenticated && isAdmin
      ? [{ label: "Admin", url: viewpaths.adminDashboardViewPath }]
      : []),
  ];

  const handleNav = (url: string) => {
    history.push(url);
    props.onClose?.();
  };

  return (
    <Drawer
      anchor="top"
      onClose={props.onClose}
      open={props.open}
      PaperProps={{
        sx: {
          width: "100%",
          maxHeight: "85vh",
          borderRadius: 0,
          borderBottom: `1px solid ${alpha(theme.palette.divider, 0.12)}`,
          backgroundColor: theme.palette.background.paper,
          boxShadow: theme.shadows[8],
        },
      }}
      variant="temporary"
    >
      <Stack sx={{ height: "100%", overflow: "auto" }}>
        {/* Header: Logo + Close */}
        <Stack
          direction="row"
          alignItems="center"
          justifyContent="space-between"
          sx={{
            px: 2,
            py: 1.5,
            minHeight: 56,
            borderBottom: `1px solid ${alpha(theme.palette.divider, 0.08)}`,
          }}
        >
          <Box
            onClick={() => handleNav(viewpaths.landingViewPath)}
            sx={{
              display: "flex",
              alignItems: "center",
              cursor: "pointer",
              "& img": { height: 36, width: "auto" },
            }}
          >
            <img src={isDark ? darkLogo : lightLogo} alt="Panacea Insights" />
          </Box>
          <IconButton
            onClick={props.onClose}
            aria-label="close menu"
            size="small"
            sx={{
              color: theme.palette.text.secondary,
              "&:hover": {
                color: theme.palette.text.primary,
                backgroundColor: alpha(theme.palette.text.primary, 0.08),
              },
            }}
          >
            <CloseIcon />
          </IconButton>
        </Stack>

        {/* Nav links */}
        <Stack component="nav" sx={{ flex: 1, py: 1, px: 1 }}>
          {navMenu.map((item) => {
            const isActive =
              pathname === item.url ||
              (item.url !== "/" && pathname.startsWith(item.url));
            return (
              <Box
                key={item.url}
                onClick={() => handleNav(item.url)}
                sx={{
                  py: 1.5,
                  px: 2,
                  borderRadius: "12px",
                  cursor: "pointer",
                  color: isActive ? BRAND : theme.palette.text.primary,
                  backgroundColor: isActive
                    ? alpha(BRAND, isDark ? 0.12 : 0.08)
                    : "transparent",
                  transition: "background-color 0.2s, color 0.2s",
                  "&:hover": {
                    backgroundColor: isActive
                      ? alpha(BRAND, isDark ? 0.18 : 0.12)
                      : alpha(theme.palette.text.primary, isDark ? 0.06 : 0.04),
                    color: isActive ? BRAND : theme.palette.text.primary,
                  },
                }}
              >
                <Typography
                  sx={{
                    fontSize: "15px",
                    fontWeight: isActive ? 600 : 500,
                    textTransform: "none",
                  }}
                >
                  {item.label}
                </Typography>
              </Box>
            );
          })}
        </Stack>

        <Divider sx={{ borderColor: alpha(theme.palette.divider, 0.08) }} />

        {/* Footer: User info when logged in, or Login */}
        <Box sx={{ p: 2 }}>
          {authenticated ? (
            <Stack spacing={1}>
              <Box
                sx={{
                  py: 1.5,
                  px: 2,
                  borderRadius: "12px",
                  backgroundColor: alpha(theme.palette.divider, 0.06),
                }}
              >
                <Typography
                  sx={{
                    fontSize: "14px",
                    fontWeight: 600,
                    color: theme.palette.text.primary,
                  }}
                >
                  {userName?.trim() || email?.split("@")[0] || "User"}
                </Typography>
                {email && (
                  <Typography
                    variant="caption"
                    sx={{ color: theme.palette.text.secondary }}
                  >
                    {email}
                  </Typography>
                )}
              </Box>
              <Box
                onClick={() => {
                  dispatch(logOutAction({} as any));
                  clearAuthUserIdFromStorage();
                  props.onClose?.();
                  const authRequiredPaths = [
                    viewpaths.dashboardViewPath,
                    viewpaths.settingsViewPath,
                    viewpaths.holidayCalenderViewPath,
                  ];
                  const isAuthRequired = authRequiredPaths.some((p) =>
                    (pathname || "/").startsWith(p)
                  );
                  if (isAuthRequired) {
                    history.push(viewpaths.landingViewPath);
                  }
                }}
                sx={{
                  display: "flex",
                  alignItems: "center",
                  justifyContent: "center",
                  py: 1.5,
                  px: 2,
                  borderRadius: "12px",
                  cursor: "pointer",
                  color: BRAND,
                  border: `1px solid ${alpha(BRAND, 0.4)}`,
                  backgroundColor: alpha(BRAND, 0.06),
                  transition: "all 0.2s",
                  "&:hover": {
                    backgroundColor: alpha(BRAND, 0.12),
                    borderColor: BRAND,
                  },
                }}
              >
                <Typography
                  sx={{
                    fontSize: "14px",
                    fontWeight: 600,
                    textTransform: "none",
                  }}
                >
                  {t("logout") || "Log out"}
                </Typography>
              </Box>
            </Stack>
          ) : (
            <Box
              onClick={() => handleNav(viewpaths.loginViewPath)}
              sx={{
                display: "flex",
                alignItems: "center",
                justifyContent: "center",
                gap: 1,
                py: 1.5,
                px: 2,
                borderRadius: "12px",
                cursor: "pointer",
                border: `1px solid ${alpha(BRAND, 0.4)}`,
                color: BRAND,
                backgroundColor: alpha(BRAND, 0.06),
                transition: "all 0.2s",
                "&:hover": {
                  backgroundColor: alpha(BRAND, 0.12),
                  borderColor: BRAND,
                },
              }}
            >
              <Typography
                sx={{
                  fontSize: "14px",
                  fontWeight: 600,
                  textTransform: "none",
                }}
              >
                {t("login") || "Login"}
              </Typography>
            </Box>
          )}
        </Box>
      </Stack>
    </Drawer>
  );
};

export default MobileNav;
